import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { meKey } from '@/features/auth/api'
import { http } from '@/lib/api/client'
import type { NotificationCategory, Resource, Staff, StaffNotification } from '@/lib/api/types'

export interface NotificationList {
  data: StaffNotification[]
  meta: {
    unread: number
    unread_by_category: Record<NotificationCategory, number>
    current_page: number
    last_page: number
    total: number
  }
}

/** The bell's unread count (and the latest notifications). */
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: ({ signal }) => http.get<NotificationList>('/notifications', undefined, signal),
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: 60_000,
  })
}

/** The Notifications page: one category (or all), optionally unread only, 25 a page. */
export function useNotificationList(
  category: NotificationCategory | 'all',
  unreadOnly: boolean,
  page: number,
) {
  return useQuery({
    queryKey: ['notifications', 'list', category, unreadOnly, page],
    queryFn: ({ signal }) =>
      http.get<NotificationList>(
        '/notifications',
        {
          category: category === 'all' ? undefined : category,
          unread: unreadOnly ? 1 : undefined,
          page,
        },
        signal,
      ),
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  })
}

/** Mark some notifications (ids), one category's, or all as read. */
export function useMarkRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { ids?: string[]; category?: NotificationCategory } = {}) =>
      http.post<{ meta: { unread: number } }>('/notifications/read', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })
}

export function useEmailPreference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (on: boolean) =>
      (await http.patch<Resource<Staff>>('/me/preferences', { email_notifications: on })).data,
    onSuccess: (staff) => queryClient.setQueryData(meKey, staff),
  })
}

/** Which kinds of notification also come by email: the categories turned off. */
export function useEmailMuted() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (muted: string[]) =>
      (await http.patch<Resource<Staff>>('/me/preferences', { email_muted: muted })).data,
    onSuccess: (staff) => queryClient.setQueryData(meKey, staff),
  })
}
